/**
 * "Email…" from Teslyn Cloud (batch 6, F14; docs/specs/cloud/app-email.md; help `cloud.app-email`): a short message to
 * people Teslyn knows (the project's crew, the staff register, the organisation's sign-ins, the client's people), sent
 * by Teslyn's mail service from "No Reply <organisation> powered by Teslyn" with replies to the sender (decisions
 * 508, 890). The words are
 * filled in for the person to change.
 */
import { useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  APP_EMAIL_BODY_MAX,
  appEmailSenderName,
  appMessageProblems,
  emailSentWords,
  type EmailRecipientChoice,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { TextArea, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import { loadRecipients, loadTenantName, sendEmail } from './api.ts';

export interface EmailDialogProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly projectId: string | null;
  readonly clientOrgId: string | null;
  readonly subject: string;
  readonly body: string;
  /** Tick the client's people from the start. */
  readonly chooseClients?: boolean;
  /** Closed: the words of what was sent, or null when cancelled. */
  readonly onClose: (sentWords: string | null) => void;
  /** The recipients (tests). */
  readonly load?: () => Promise<EmailRecipientChoice[]>;
}

export function EmailDialog(props: EmailDialogProps): ReactElement {
  const { sb, tenantId, projectId, clientOrgId, onClose, chooseClients, load } = props;
  const [choices, setChoices] = useState<EmailRecipientChoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [keys, setKeys] = useState<readonly string[]>([]);
  const [subject, setSubject] = useState(props.subject);
  const [body, setBody] = useState(props.body);
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [tenantName, setTenantName] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void loadTenantName(sb, tenantId).then((n) => live && setTenantName(n));
    return () => {
      live = false;
    };
  }, [sb, tenantId]);

  useEffect(() => {
    let live = true;
    (load ?? (() => loadRecipients(sb, tenantId, projectId, clientOrgId)))()
      .then((c) => {
        if (!live) return;
        setChoices(c);
        if (chooseClients)
          setKeys(c.filter((x) => x.hasEmail && x.why.endsWith('(client)')).map((x) => x.key));
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [sb, tenantId, projectId, clientOrgId, chooseClients, load]);

  async function send(): Promise<void> {
    const p = appMessageProblems({ subject, body, recipients: keys.length });
    setProblems(p);
    if (p.length) return;
    setBusy(true);
    try {
      const r = await sendEmail(sb, { tenantId, projectId, keys, subject, body });
      onClose(emailSentWords(r, tenantName));
    } catch (e) {
      setProblems([e instanceof Error ? e.message : String(e)]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel confirm stack email-dialog" role="dialog" aria-labelledby="email-dialog-title">
      <h2 className="heading" id="email-dialog-title">
        Email
      </h2>
      <p className="caption muted">
        Sent from &quot;{appEmailSenderName(tenantName)}&quot;; a reply comes back to you. Only people Teslyn
        knows can be chosen.
      </p>
      {error && <ErrorState message={error} />}
      {!error && !choices && <Loading what="who it can go to" />}
      {choices && (
        <fieldset className="step stack">
          <legend className="label">To</legend>
          <ul className="email-dialog__people">
            {choices.map((c) => (
              <li key={c.key}>
                <label className="row row--wrap check">
                  <input
                    type="checkbox"
                    checked={keys.includes(c.key)}
                    disabled={!c.hasEmail}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setKeys((k) => (on ? [...k, c.key] : k.filter((x) => x !== c.key)));
                    }}
                  />
                  <span className="body">{c.name}</span>
                  <span className="caption muted">
                    {c.why}
                    {c.hasEmail ? '' : ' · no email address'}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      )}
      <TextField label="Subject" value={subject} onChange={setSubject} />
      <TextArea
        label="Message"
        value={body}
        onChange={setBody}
        rows={8}
        hint={`Up to ${APP_EMAIL_BODY_MAX} characters. Who sent it and how to reply are added at the end.`}
      />
      {problems.length > 0 && (
        <ul className="notice notice--fail" role="alert">
          {problems.map((p) => (
            <li key={p} className="body">
              {p}
            </li>
          ))}
        </ul>
      )}
      <div className="row row--wrap">
        <Button variant="primary" busy={busy} onClick={() => void send()}>
          {busy ? 'Sending…' : `Send to ${keys.length} ${keys.length === 1 ? 'person' : 'people'}`}
        </Button>
        <Button variant="quiet" onClick={() => onClose(null)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
